import React, { useState, useMemo, useEffect, useRef } from "react";
import { Card, CardBody } from "reactstrap";
import CommonTableIndexWise, { Column } from "Components/Common/CommonTableIndexWise";
import { PERMISSION_MODULES } from "common/constants/PermissionConstants";
import TruncatedText from "Components/Common/TruncatedText";
import { DndContext, closestCenter, DragEndEvent } from "@dnd-kit/core";
import {
  SortableContext,
  verticalListSortingStrategy,
  arrayMove,
} from "@dnd-kit/sortable";
import styles from "../css/addstudy.module.scss";
import { crfstepcolumns } from "../helpers/studyconstant";
import { CHECKLIST_CATEGORY_OPTIONS, loadCodelistsDetailId } from "api/modules/common.option";

interface CRFAssignmentStepProps {
  control?: any;
  setValue?: any;
  watch?: any;
  rawCRFData?: any[];
  crfLoading?: boolean;
  existingStudyCRFs?: any[];
  studyCRFAssignmentQuery?: any;
}

// Reusable scroll wrapper component for horizontal chip rows
const HorizontalChipBar: React.FC<{ children: React.ReactNode; id: string }> = ({ children, id }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const checkScroll = () => {
    const el = containerRef.current;
    if (!el) return;
    const { scrollLeft, scrollWidth, clientWidth } = el;
    setCanScrollLeft(scrollLeft > 2);
    setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 2);
  };

  useEffect(() => {
    checkScroll();
    const el = containerRef.current;
    if (el) {
      el.addEventListener("scroll", checkScroll);
      window.addEventListener("resize", checkScroll);
    }
    return () => {
      if (el) el.removeEventListener("scroll", checkScroll);
      window.removeEventListener("resize", checkScroll);
    };
  }, [children]);

  const scroll = (direction: "left" | "right") => {
    const el = containerRef.current;
    if (!el) return;
    const firstChip = el.firstElementChild as HTMLElement | null;
    const scrollAmount = firstChip ? firstChip.offsetWidth + 8 : 150;
    el.scrollBy({
      left: direction === "left" ? -scrollAmount : scrollAmount,
      behavior: "smooth",
    });
  };

  return (
    <div className={`card border shadow-sm ${styles.horizontalChipBar}`}>
      <button
        type="button"
        disabled={!canScrollLeft}
        onClick={() => scroll("left")}
        className={styles.scrollArrowBtn}
      >
        <i className="ri-arrow-left-s-line" />
      </button>

      <div id={id} ref={containerRef} className={styles.chipScrollContainer}>
        {children}
      </div>

      <button
        type="button"
        disabled={!canScrollRight}
        onClick={() => scroll("right")}
        className={styles.scrollArrowBtn}
      >
        <i className="ri-arrow-right-s-line" />
      </button>
    </div>
  );
};

const CRFAssignmentStep: React.FC<CRFAssignmentStepProps> = ({
  setValue,
  watch,
  existingStudyCRFs,
  studyCRFAssignmentQuery,
}) => {
  const mode = "periodspecific";

  const watchedNumPeriods = watch ? watch("numberofperiods") : 4;
  const numPeriods = Number(watchedNumPeriods) || 1;

  const [selectedPeriod, setSelectedPeriod] = useState<number>(1);
  const [selectedCategory, setSelectedCategory] = useState<number>();

  // Custom ordered or modified list per period/category context
  const [customCRFs, setCustomCRFs] = useState<Record<string, any[]>>({});

  // Hidden categories map (scoped per period context)
  const [hiddenCategoriesState, setHiddenCategoriesState] = useState<Record<string, number[]>>({});
  const [showAddCategoryDropdown, setShowAddCategoryDropdown] = useState(false);
  const addCategoryDropdownRef = useRef<HTMLDivElement>(null);

  const hiddenContextKey = mode === "periodspecific" ? `p${selectedPeriod}` : "common";

  const currentHiddenSet = useMemo(() => {
    const list = hiddenCategoriesState[hiddenContextKey] || [];
    return new Set<number>(list);
  }, [hiddenCategoriesState, hiddenContextKey]);

  const [studyCategoryValue, setStudyCategoryValue] = useState<number | string | undefined>();

  useEffect(() => {
    const found = CHECKLIST_CATEGORY_OPTIONS.find(
      (opt) => opt.label.toLowerCase() === "study"
    );
    if (found) {
      setStudyCategoryValue(found.value);
    }
  }, [CHECKLIST_CATEGORY_OPTIONS.length]);

  const assignedList = useMemo(() => {
    const raw = (studyCRFAssignmentQuery?.data as any)?.data;
    let list: any[] = [];
    if (Array.isArray(raw)) {
      raw.forEach((item: any) => {
        if (Array.isArray(item.assigned)) {
          list = list.concat(item.assigned);
        } else if (item.crfid || item.id) {
          list.push(item);
        }
      });
    } else if (raw && Array.isArray(raw.assigned)) {
      list = raw.assigned;
    }
    // Filter to show ONLY modules & CRFs under the "study" category in this step
    return list.filter((item: any) => {
      const catName = (item.crfcategoryname || item.categoryname || "").toLowerCase().trim();
      if (catName) {
        return catName === "study" || (catName.includes("study") && !catName.includes("ambulatory"));
      }
      return true;
    });
  }, [studyCRFAssignmentQuery?.data]);

  const activeCategories = useMemo(() => {
    const map = new Map<number, { label: string; value: number; crfcategory: number }>();
    assignedList.forEach((item: any) => {
      const modId = Number(item.moduleid);
      if (modId && !map.has(modId)) {
        map.set(modId, {
          label: item.crfmodulename || item.modulename || `Module ${modId}`,
          value: modId,
          crfcategory: Number(item.crfcategory),
        });
      }
    });
    return Array.from(map.values());
  }, [assignedList]);

  const allCRFsData = useMemo(() => {
    const dataMap: Record<number, any[]> = {};
    assignedList.forEach((item: any) => {
      const modId = Number(item.moduleid);
      if (modId) {
        if (!dataMap[modId]) {
          dataMap[modId] = [];
        }
        if (!dataMap[modId].some((c: any) => (c.crfid || c.id) === (item.crfid || item.id))) {
          dataMap[modId].push(item);
        }
      }
    });
    return dataMap;
  }, [assignedList]);

  const rawCRFData = useMemo(() => {
    if (!selectedCategory) return [];
    return allCRFsData[selectedCategory] || [];
  }, [allCRFsData, selectedCategory]);

  const selectedCategoryLabel = useMemo(() => {
    const found = activeCategories.find((c: any) => Number(c.value) === selectedCategory);
    return found ? found.label : "N/A";
  }, [activeCategories, selectedCategory]);

  // Auto-select first active category on load
  useEffect(() => {
    if (
      activeCategories.length > 0 &&
      (!selectedCategory || !activeCategories.some((t: any) => Number(t.value) === selectedCategory))
    ) {
      setSelectedCategory(Number(activeCategories[0].value));
    }
  }, [activeCategories, selectedCategory]);

  // If selected category is hidden, auto-select first visible category
  useEffect(() => {
    if (selectedCategory && currentHiddenSet.has(Number(selectedCategory))) {
      const firstVisible = activeCategories.find((t: any) => !currentHiddenSet.has(t.value));
      if (firstVisible) setSelectedCategory(Number(firstVisible.value));
    }
  }, [currentHiddenSet, activeCategories, selectedCategory]);

  const periodList = useMemo(() => {
    return Array.from({ length: Math.max(1, numPeriods) }, (_, i) => i + 1);
  }, [numPeriods]);

  useEffect(() => {
    if (selectedPeriod > numPeriods) {
      setSelectedPeriod(1);
    }
  }, [numPeriods, selectedPeriod]);

  // Storage key context: e.g. "p1_571"
  const currentKey = useMemo(() => {
    return `p${selectedPeriod}_${selectedCategory}`;
  }, [selectedPeriod, selectedCategory]);

  const [assignedState, setAssignedState] = useState<Record<string, Record<number, boolean>>>({});
  const isEditMode = existingStudyCRFs !== undefined;

  const periods = watch ? watch("periods") : [];

  // Build existing assignments map (Edit mode lookup)
  const existingSavedMap = useMemo(() => {
    if (!existingStudyCRFs || !Array.isArray(existingStudyCRFs)) return null;

    const periodIdToNoMap = new Map<number, number>();
    if (Array.isArray(periods)) {
      periods.forEach((period: any, index: number) => {
        const pId = Number(period.periodid || period.period_id || period.id);
        if (pId) {
          periodIdToNoMap.set(pId, index + 1);
        }
      });
    }

    const map: Record<string, { masterIds: Set<number>; savedMasterMap: Map<number, any> }> = {};

    const ensureKey = (key: string) => {
      if (!map[key]) {
        map[key] = {
          masterIds: new Set<number>(),
          savedMasterMap: new Map<number, any>(),
        };
      }
      return map[key];
    };

    existingStudyCRFs.forEach((item: any) => {
      const savedPeriodId = Number(item.periodid);
      const periodNoFromId = savedPeriodId ? periodIdToNoMap.get(savedPeriodId) : undefined;
      const periodVal = periodNoFromId || Number(item.period_sort || item.period_no) || 1;
      const categoryVal = Number(item.crfmodule || item.moduleid || item.crfcategory);
      const crfId = Number(item.crfid || item.id);

      const periodKey = `p${periodVal}_${categoryVal}`;
      const target = ensureKey(periodKey);

      if (crfId) {
        target.masterIds.add(crfId);
        target.savedMasterMap.set(crfId, item);
      }
    });

    return map;
  }, [existingStudyCRFs, periods]);

  const handleToggleAssign = (id: number) => {
    setAssignedState((prev) => {
      const currentMap = prev[currentKey] || {};
      const currentVal = currentCRFs.find((i: any) => i.id === id)?.assigned;
      return {
        ...prev,
        [currentKey]: {
          ...currentMap,
          [id]: !currentVal,
        },
      };
    });
  };

  const currentCRFs = useMemo(() => {
    if (!selectedCategory) return [];

    const currentMap = assignedState[currentKey] || {};
    const savedContext = existingSavedMap ? existingSavedMap[currentKey] : null;

    let list: any[] = [];

    if (customCRFs[currentKey]) {
      list = customCRFs[currentKey];
    } else {
      list = rawCRFData.map((masterItem: any) => {
        const crfId = Number(masterItem.crfid || masterItem.crfId || masterItem.id);
        const savedItem = savedContext?.savedMasterMap.get(crfId);

        if (savedItem) {
          return {
            ...masterItem,
            studycrfid: Number(savedItem.studycrfid) || null,
            name: savedItem.crfname || savedItem.crfName || masterItem.name || masterItem.crfname || masterItem.crfName || `CRF #${crfId}`,
            displayorder: savedItem.displayorder || masterItem.displayorder,
            assigned: true,
          };
        }

        return {
          ...masterItem,
          studycrfid: null,
          assigned: false,
        };
      });

      list.sort((a, b) => {
        const orderA = Number(a.displayorder) || 999;
        const orderB = Number(b.displayorder) || 999;
        return orderA - orderB;
      });
    }

    return list.map((item: any, idx: number) => {
      const trackingId = Number(item.crfid || item.crfId || item.id);
      let isAssigned = item.assigned;

      if (currentMap[trackingId] !== undefined) {
        isAssigned = currentMap[trackingId];
      } else if (isEditMode) {
        isAssigned = savedContext?.masterIds.has(trackingId) ?? false;
      } else {
        isAssigned = item.assigned !== undefined ? item.assigned : false;
      }

      const displayOrderVal = item.displayorder !== undefined && item.displayorder !== null
        ? String(item.displayorder).padStart(2, "0")
        : String(idx + 1).padStart(2, "0");

      return {
        ...item,
        id: trackingId,
        crfid: trackingId,
        name: item.name || item.crfname || item.crfName || `CRF #${trackingId}`,
        assigned: isAssigned,
        displayorder: displayOrderVal,
      };
    });
  }, [assignedState, currentKey, customCRFs, rawCRFData, selectedCategory, existingSavedMap, isEditMode]);

  const isAllAssigned = useMemo(() => {
    if (currentCRFs.length === 0) return false;
    return currentCRFs.every((item: any) => item.assigned);
  }, [currentCRFs]);

  const handleToggleSelectAll = () => {
    const targetVal = !isAllAssigned;
    setAssignedState((prev) => {
      const currentMap = { ...(prev[currentKey] || {}) };
      currentCRFs.forEach((item: any) => {
        currentMap[item.id] = targetVal;
      });
      return {
        ...prev,
        [currentKey]: currentMap,
      };
    });
  };

  const handleDragEnd = (event: DragEndEvent): void => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const activeIndex = currentCRFs.findIndex((item: any) => item.id === active.id);
    const overIndex = currentCRFs.findIndex((item: any) => item.id === over.id);

    if (activeIndex === -1 || overIndex === -1) return;

    const reorderedList = arrayMove(currentCRFs, activeIndex, overIndex).map(
      (item: any, idx: number) => ({
        ...item,
        displayorder: String(idx + 1).padStart(2, "0"),
      })
    );

    setCustomCRFs((prev) => ({
      ...prev,
      [currentKey]: reorderedList,
    }));
  };

  const getCRFCountForCategory = (catVal: number) => {
    const key = `p${selectedPeriod}_${catVal}`;
    const currentMap = assignedState[key] || {};
    const savedContext = existingSavedMap ? existingSavedMap[key] : null;

    let items: any[] = [];
    if (customCRFs[key]) {
      items = customCRFs[key];
    } else {
      items = allCRFsData[catVal] || [];
    }

    return items.filter((item: any) => {
      const trackingId = Number(item.crfid || item.crfId || item.id);
      if (currentMap[trackingId] !== undefined) return currentMap[trackingId];
      if (isEditMode) {
        return savedContext?.masterIds.has(trackingId) ?? false;
      }
      return item.assigned !== undefined ? item.assigned : false;
    }).length;
  };

  const handleHideCategory = (catVal: number) => {
    setHiddenCategoriesState((prev) => {
      const currentList = prev[hiddenContextKey] || [];
      if (currentList.includes(catVal)) return prev;
      return {
        ...prev,
        [hiddenContextKey]: [...currentList, catVal],
      };
    });
    setShowAddCategoryDropdown(false);
  };

  const handleRestoreCategory = (catVal: number) => {
    setHiddenCategoriesState((prev) => {
      const currentList = prev[hiddenContextKey] || [];
      return {
        ...prev,
        [hiddenContextKey]: currentList.filter((v) => v !== catVal),
      };
    });
    setShowAddCategoryDropdown(false);
    setSelectedCategory(catVal);
  };

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (addCategoryDropdownRef.current && !addCategoryDropdownRef.current.contains(e.target as Node)) {
        setShowAddCategoryDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);


  const configuredCRFsData = useMemo(() => {
    return {
      assignedState,
      customCRFs,
      hiddenCategoriesState,
      isEditMode,
      existingStudyCRFs,
      allCRFsData,
      activeCategories,
    };
  }, [assignedState, customCRFs, hiddenCategoriesState, isEditMode, existingStudyCRFs, allCRFsData, activeCategories]);

  const prevConfiguredCRFsDataStrRef = useRef<string>("");

  useEffect(() => {
    if (setValue) {
      const serialized = JSON.stringify(configuredCRFsData);
      if (serialized !== prevConfiguredCRFsDataStrRef.current) {
        prevConfiguredCRFsDataStrRef.current = serialized;
        setValue("configuredCRFsData", configuredCRFsData);
      }
    }
  }, [configuredCRFsData, setValue]);

  const visibleCategories = activeCategories.filter((t: any) => !currentHiddenSet.has(t.value));
  const hiddenCategories = activeCategories.filter((t: any) => currentHiddenSet.has(t.value));

  return (
    <div>
      {/* Period Selector Chips */}
      <div className="mb-3">
        <div className="fw-medium text-muted mb-2 fs-14">Select Period</div>
        <HorizontalChipBar id="period-chips">
          {periodList.map((p) => {
            const isSelected = selectedPeriod === p;
            return (
              <div
                key={p}
                onClick={() => {
                  setSelectedPeriod(p);
                }}
                className={`${styles.periodChipBase} ${isSelected ? styles.periodChipSelected : ""}`}
              >
                <i
                  className={`${isSelected ? "ri-checkbox-circle-fill" : "ri-calendar-line"} me-2`}
                  style={{ fontSize: "15px" }}
                />
                Period {p}
              </div>
            );
          })}
        </HorizontalChipBar>
      </div>

      {/* CRF Category Chips */}
      <div className="mb-3">
        <div className="d-flex align-items-center justify-content-between mb-2">
          <span className="fw-medium text-muted fs-14">Select CRF Category</span>

          {/* ＋ button */}
          <div className="position-relative" ref={addCategoryDropdownRef}>
            <button
              type="button"
              title={hiddenCategories.length === 0 ? "No hidden categories" : "Restore hidden categories"}
              disabled={hiddenCategories.length === 0}
              onClick={() => setShowAddCategoryDropdown((prev) => !prev)}
              className={styles.addTypeHeaderBtn}
            >
              <i className="ri-add-line" style={{ fontSize: "14px" }} />
              {hiddenCategories.length > 0 && (
                <span className={styles.badgeCounter}>{hiddenCategories.length}</span>
              )}
            </button>

            {/* Hidden categories dropdown */}
            {showAddCategoryDropdown && hiddenCategories.length > 0 && (
              <div className={styles.dropdownPopover}>
                <div className={styles.dropdownHeader}>Hidden Categories</div>
                {hiddenCategories.map((cat: any) => (
                  <button
                    key={cat.value}
                    type="button"
                    onClick={() => handleRestoreCategory(cat.value)}
                    className={styles.dropdownItemBtn}
                  >
                    <i className="ri-file-list-3-line text-primary fs-15 flex-shrink-0" />
                    <div className="flex-grow-1 overflow-hidden">
                      <TruncatedText text={cat.label} maxWidth="140px" />
                    </div>
                    <span className="text-muted fs-11 ms-auto flex-shrink-0">
                      ({getCRFCountForCategory(cat.value)})
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Scrollable chip row */}
        <HorizontalChipBar id="crf-category-chips">
          {visibleCategories.map((cat: any) => {
            const count = getCRFCountForCategory(Number(cat.value));
            const isSelected = Number(selectedCategory) === Number(cat.value);
            return (
              <div
                key={cat.value}
                className={`${styles.chipBase} ${isSelected ? styles.chipSelected : ""}`}
              >
                <span
                  onClick={() => setSelectedCategory(Number(cat.value))}
                  className="d-inline-flex align-items-center gap-1"
                >
                  <i
                    className={`${isSelected ? "ri-checkbox-circle-fill" : "ri-file-list-3-line"}`}
                    style={{ fontSize: "15px" }}
                  />
                  {cat.label} ({count})
                </span>

                {/* ✕ button */}
                <button
                  type="button"
                  title={isSelected ? "Cannot remove active category" : `Hide ${cat.label}`}
                  className={styles.chipCloseBtn}
                  disabled={isSelected}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (!isSelected) handleHideCategory(cat.value);
                  }}
                >
                  <i className="ri-close-line" style={{ fontSize: "13px" }} />
                </button>
              </div>
            );
          })}
        </HorizontalChipBar>
      </div>

      {/* CRF Table Section */}
      <Card className="border shadow-sm mb-4">
        <CardBody className="p-3">
          <div className="d-flex justify-content-between align-items-center mb-2 flex-wrap gap-2">
            <div className="d-flex align-items-center gap-2 small">
              <span className="badge bg-primary-subtle text-primary px-3 py-2 rounded-pill fw-semibold fs-12">
                Period {selectedPeriod}
              </span>
              <i className="ri-arrow-right-s-line text-muted"></i>
              <span className="badge bg-primary-subtle text-primary px-3 py-2 rounded-pill fw-semibold fs-12">
                {selectedCategoryLabel}
              </span>
            </div>
          </div>

          <div className={styles.checklistTableWrapper}>
            <DndContext collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
              <SortableContext
                items={currentCRFs.map((item: any) => item.id)}
                strategy={verticalListSortingStrategy}
              >
                <CommonTableIndexWise
                  moduleName={PERMISSION_MODULES.STUDY}
                  columns={crfstepcolumns(isAllAssigned, currentCRFs, handleToggleSelectAll, handleToggleAssign)}
                  data={currentCRFs}
                  paginationShow={false}
                  showStatus={false}
                  loading={studyCRFAssignmentQuery?.isLoading || studyCRFAssignmentQuery?.isFetching}
                  dragAndDrop
                  hideAction={true}
                />
              </SortableContext>
            </DndContext>
          </div>
        </CardBody>
      </Card>
    </div>
  );
};

export default CRFAssignmentStep;
